import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Avatar } from '../Avatar/Avatar';

import styles from './KarlIntro.module.css';

interface KarlIntroProps {
  /**
   * Реплика Karl — **фиксированная строка из локалей**, переведённая экраном.
   * LLM её не пишет, и реплика не зависит от доступности модели
   * (DESIGN_SYSTEM §5.4). Допустима инлайн-разметка (`<Trans>` со `<strong>`),
   * но не блоки: текст стоит в одном абзаце.
   */
  children: ReactNode;
  className?: string | undefined;
}

/**
 * Реплика экрана (DESIGN_SYSTEM §5.4, §7.2): на экранах воронки она заменяет
 * вводный абзац под заголовком. Заголовок экрана остаётся над ней и в
 * компонент не входит.
 *
 * Состав — тот же, что у реплики Karl в чате, но без времени: фото со
 * свечением слева, голубой пузырь справа во всю оставшуюся ширину, в пузыре
 * имя и маркировка «KI-Assistent», под ними текст.
 *
 * **Маркировка обязательна** — это требование прозрачности AI (MVP_PRD §26), а
 * не подпись: фотореалистичное лицо без неё читается как живой человек. Она
 * стоит текстом, а изображение декоративно (`Avatar` даёт `alt=""` и
 * `aria-hidden`): имя и роль скринридер читает из подписи.
 *
 * Это обычный текстовый блок, а не live-region: реплика стоит на экране с
 * первой отрисовки и не сообщает о событии.
 *
 * Размер фото меняется медиазапросом, а у `Avatar` размер — свойство, поэтому
 * в разметке два аватара, и виден ровно один: md от 768 px, sm уже.
 */
export function KarlIntro({ children, className }: KarlIntroProps) {
  const { t } = useTranslation();

  return (
    <div className={[styles.intro, className].filter(Boolean).join(' ')}>
      <span className={styles.avatarWide}>
        <Avatar variant="karl" size="md" />
      </span>
      <span className={styles.avatarNarrow}>
        <Avatar variant="karl" size="sm" />
      </span>
      <div className={styles.bubble}>
        {/* Маркировка AI: обязательна, не декоративна. */}
        <p className={styles.author}>
          <span className={styles.authorName}>{t('app.karl.name')}</span>
          <span className={styles.authorTag}>{t('app.karl.role')}</span>
        </p>
        <p className={styles.text}>{children}</p>
      </div>
    </div>
  );
}
